import { useEffect, useState, useCallback } from "react"
import { getBonesBalance, getBonesLifetime, BONES_EVENT } from "./bones-store"
import {
  getOwned, getEquipped, WARDROBE_EVENT, type Equipped,
} from "./wardrobe-store"
import { initWardrobeSync } from "./wardrobe-sync"
import { useAuth } from "@/lib/auth/auth-context"

interface WardrobeSnapshot {
  balance: number
  lifetime: number
  owned: string[]
  equipped: Equipped
}

function snapshot(): WardrobeSnapshot {
  return {
    balance: getBonesBalance(),
    lifetime: getBonesLifetime(),
    owned: getOwned(),
    equipped: getEquipped(),
  }
}

/** Full reactive view of Bones + wardrobe (balance, owned, equipped). */
export function useWardrobe(): WardrobeSnapshot & { reload: () => void } {
  const [state, setState] = useState<WardrobeSnapshot>(snapshot)
  const reload = useCallback(() => setState(snapshot()), [])
  useEffect(() => {
    const on = () => reload()
    window.addEventListener(BONES_EVENT, on)
    window.addEventListener(WARDROBE_EVENT, on)
    window.addEventListener("storage", on)
    return () => {
      window.removeEventListener(BONES_EVENT, on)
      window.removeEventListener(WARDROBE_EVENT, on)
      window.removeEventListener("storage", on)
    }
  }, [reload])
  return { ...state, reload }
}

/** Lightweight subscription to just the equipped outfit (used by the Biscuit visuals). */
export function useEquipped(): Equipped {
  const [eq, setEq] = useState<Equipped>(getEquipped)
  useEffect(() => {
    const on = () => setEq(getEquipped())
    window.addEventListener(WARDROBE_EVENT, on)
    window.addEventListener("storage", on)
    return () => {
      window.removeEventListener(WARDROBE_EVENT, on)
      window.removeEventListener("storage", on)
    }
  }, [])
  return eq
}

/** Mount once in the authenticated shell: hydrate the wardrobe/Bones from
 * Supabase and write local changes through. localStorage stays the instant
 * source of truth; this just keeps it in sync across devices. */
export function useWardrobeSync(): void {
  const { user } = useAuth()
  const userId = user?.id
  useEffect(() => {
    if (!userId) return
    const teardown = initWardrobeSync()
    return teardown
  }, [userId])
}

/** Just the Bones balance — for the dashboard pill. */
export function useBones(): { balance: number; lifetime: number } {
  const [b, setB] = useState(() => ({ balance: getBonesBalance(), lifetime: getBonesLifetime() }))
  useEffect(() => {
    const on = () => setB({ balance: getBonesBalance(), lifetime: getBonesLifetime() })
    window.addEventListener(BONES_EVENT, on)
    window.addEventListener("storage", on)
    return () => {
      window.removeEventListener(BONES_EVENT, on)
      window.removeEventListener("storage", on)
    }
  }, [])
  return b
}
